// A synchronous zlib / raw DEFLATE decoder (RFC 1950 / 1951): the PDF reader decodes every stream as it meets it.

const LEN_BASE = [3, 4, 5, 6, 7, 8, 9, 10, 11, 13, 15, 17, 19, 23, 27, 31, 35, 43, 51, 59, 67, 83, 99, 115, 131, 163, 195, 227, 258];
const LEN_EXTRA = [0, 0, 0, 0, 0, 0, 0, 0, 1, 1, 1, 1, 2, 2, 2, 2, 3, 3, 3, 3, 4, 4, 4, 4, 5, 5, 5, 5, 0];
const DIST_BASE = [1, 2, 3, 4, 5, 7, 9, 13, 17, 25, 33, 49, 65, 97, 129, 193, 257, 385, 513, 769, 1025, 1537, 2049, 3073, 4097, 6145, 8193, 12289, 16385, 24577];
const DIST_EXTRA = [0, 0, 0, 0, 1, 1, 2, 2, 3, 3, 4, 4, 5, 5, 6, 6, 7, 7, 8, 8, 9, 9, 10, 10, 11, 11, 12, 12, 13, 13];
const CL_ORDER = [16, 17, 18, 0, 8, 7, 9, 6, 10, 5, 11, 4, 12, 3, 13, 2, 14, 1, 15];

interface Huff { counts: Uint16Array; symbols: Uint16Array }

function build(lengths: ArrayLike<number>, n: number): Huff {
    const counts = new Uint16Array(16);
    for (let i = 0; i < n; i++) counts[lengths[i]!]!++;
    counts[0] = 0;
    const offs = new Uint16Array(16);
    for (let i = 1; i < 16; i++) offs[i] = offs[i - 1]! + counts[i - 1]!;
    const symbols = new Uint16Array(n);
    for (let i = 0; i < n; i++) if (lengths[i]) symbols[offs[lengths[i]!]!++] = i;
    return { counts, symbols };
}

let fixedLit: Huff | null = null;
let fixedDist: Huff | null = null;

/** Inflates raw DEFLATE data starting at `pos`. A truncated or damaged stream returns what was decoded so far. */
export function inflateRaw(src: Uint8Array, pos = 0): Uint8Array {
    let out = new Uint8Array(Math.max(1024, src.length * 4));
    let o = 0;
    let bitBuf = 0;
    let bitCnt = 0;
    const need = (n: number) => {
        while (bitCnt < n) {
            if (pos >= src.length) throw new RangeError("eof");
            bitBuf |= src[pos++]! << bitCnt;
            bitCnt += 8;
        }
    };
    const bits = (n: number): number => {
        if (n === 0) return 0;
        need(n);
        const v = bitBuf & ((1 << n) - 1);
        bitBuf >>>= n;
        bitCnt -= n;
        return v;
    };
    const decode = (h: Huff): number => {
        let code = 0, first = 0, index = 0;
        for (let len = 1; len < 16; len++) {
            code |= bits(1);
            const count = h.counts[len]!;
            if (code - count < first) return h.symbols[index + (code - first)]!;
            index += count;
            first += count;
            first <<= 1;
            code <<= 1;
        }
        throw new Error("bad code");
    };
    const ensure = (extra: number) => {
        if (o + extra > out.length) {
            const n = new Uint8Array(Math.max(out.length * 2, o + extra));
            n.set(out);
            out = n;
        }
    };
    try {
        let last = 0;
        while (!last) {
            last = bits(1);
            const type = bits(2);
            if (type === 0) {
                bitBuf = 0;
                bitCnt = 0;
                if (pos + 4 > src.length) break;
                const len = src[pos]! | (src[pos + 1]! << 8);
                pos += 4;
                ensure(len);
                const n = Math.min(len, src.length - pos);
                out.set(src.subarray(pos, pos + n), o);
                o += n;
                pos += n;
            } else if (type === 1 || type === 2) {
                let lit: Huff, dist: Huff;
                if (type === 1) {
                    if (!fixedLit) {
                        const l = new Uint8Array(288);
                        for (let i = 0; i < 144; i++) l[i] = 8;
                        for (let i = 144; i < 256; i++) l[i] = 9;
                        for (let i = 256; i < 280; i++) l[i] = 7;
                        for (let i = 280; i < 288; i++) l[i] = 8;
                        fixedLit = build(l, 288);
                        fixedDist = build(new Uint8Array(30).fill(5), 30);
                    }
                    lit = fixedLit;
                    dist = fixedDist!;
                } else {
                    const nlen = bits(5) + 257, ndist = bits(5) + 1, ncode = bits(4) + 4;
                    const cl = new Uint8Array(19);
                    for (let i = 0; i < ncode; i++) cl[CL_ORDER[i]!] = bits(3);
                    const clh = build(cl, 19);
                    const lengths = new Uint8Array(nlen + ndist);
                    let i = 0;
                    while (i < nlen + ndist) {
                        const sym = decode(clh);
                        if (sym < 16) lengths[i++] = sym;
                        else {
                            let prev = 0, rep: number;
                            if (sym === 16) { prev = lengths[i - 1]!; rep = 3 + bits(2); }
                            else if (sym === 17) rep = 3 + bits(3);
                            else rep = 11 + bits(7);
                            while (rep-- && i < nlen + ndist) lengths[i++] = prev;
                        }
                    }
                    lit = build(lengths.subarray(0, nlen), nlen);
                    dist = build(lengths.subarray(nlen), ndist);
                }
                for (;;) {
                    const sym = decode(lit);
                    if (sym < 256) { ensure(1); out[o++] = sym; }
                    else if (sym === 256) break;
                    else {
                        const s = sym - 257;
                        const len = LEN_BASE[s]! + bits(LEN_EXTRA[s]!);
                        const ds = decode(dist);
                        const d = DIST_BASE[ds]! + bits(DIST_EXTRA[ds]!);
                        if (d > o) throw new Error("bad distance");
                        ensure(len);
                        for (let k = 0; k < len; k++, o++) out[o] = out[o - d]!;
                    }
                }
            } else throw new Error("bad block");
        }
    } catch {
        // damaged or truncated: keep what came out
    }
    return out.subarray(0, o);
}

/** zlib-wrapped data (a 2-byte header, then DEFLATE); falls back to raw DEFLATE when the header is not zlib's. */
export function inflate(src: Uint8Array): Uint8Array {
    if (src.length >= 2 && (src[0]! & 0x0f) === 8 && ((src[0]! << 8) | src[1]!) % 31 === 0) return inflateRaw(src, 2 + ((src[1]! & 0x20) ? 4 : 0));
    return inflateRaw(src, 0);
}
